/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { Fragment } from "react";

import { radius, spacing } from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { BlockProps } from "../../../refactor/Block/Block.emotion";
import { Block } from "../../../refactor/Block/Block.emotion";
import type { TriviaMediumProps } from "./TriviaMedium.emotion";
import { TriviaMedium } from "./TriviaMedium.emotion";
import { TriviaScore } from "./TriviaScore.emotion";

const actionsStyle: Interpolation = {
  marginTop: spacing.m,
};
const blockStyle: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
};
const descriptionStyle: Interpolation = {
  paddingTop: spacing.s,
};
const imgContainerStyle: Interpolation<{
  color: TriviaMediumProps["color"];
}> = (props) => ({
  alignItems: "center",
  backgroundColor: props.color,
  borderBottomRightRadius: radius.l,
  borderTopRightRadius: radius.l,
  display: "flex",
  justifyContent: "center",
});
const triviaMediumStyle: Interpolation = {
  display: "block",
  [cssMediaQueryMinWidthTablet]: { display: "none" },
};

export type TriviaLargeProps = Pick<
  TriviaMediumProps,
  | "activeState"
  | "asChild"
  | "centerActions"
  | "children"
  | "color"
  | "description"
  | "focusState"
  | "hoverState"
  | "imgSrc"
  | "game"
  | "tags"
  | "title"
> &
  Pick<BlockProps, "actions" | "descriptionProps">;

export const TriviaLarge = ({
  actions,
  activeState = false,
  asChild,
  centerActions,
  children,
  color = "black",
  description,
  descriptionProps,
  focusState = true,
  hoverState = true,
  imgSrc,
  game,
  tags,
  title,
}: TriviaLargeProps) => {
  const t = useTranslations();

  const questions = game?.questions ?? null;
  const summary = game?.summary ?? null;

  return (
    <Fragment>
      <Block
        actions={actions}
        actionsCSS={actionsStyle}
        activeState={activeState}
        color={color}
        description={description ?? t("trivia_sub_title")}
        descriptionCSS={descriptionStyle}
        descriptionProps={descriptionProps}
        focusState={focusState}
        hoverState={hoverState}
        imgContainerCSS={imgContainerStyle({ color })}
        imgContainerProps={{
          children:
            questions && summary ? (
              <TriviaScore
                color={color}
                questions={questions}
                size="l"
                summary={summary}
              />
            ) : null,
        }}
        imgSrc={questions && summary ? null : imgSrc}
        tags={tags}
        title={title ?? t("trivia_section_title")}
        css={blockStyle}
      />
      <TriviaMedium
        activeState={activeState}
        asChild={asChild}
        centerActions={centerActions}
        children={children}
        color={color}
        description={description}
        focusState={focusState}
        hoverState={hoverState}
        imgSrc={imgSrc}
        game={game}
        tags={tags}
        title={title}
        css={triviaMediumStyle}
      />
    </Fragment>
  );
};
